import { useCurrentUser } from '@/hooks/useCurrentUser';
import { RowActionKind, ToolbarAction } from '@/components/NotificationTable/types';
import { ProviderMonitorDropdown } from '@/components/ProviderMonitorDropdown';
import { SearchInput } from '@/components/SearchInput';
import { NotificationStatus } from '@bariendo/lib/constants';
import { moveNotificationsToInbox } from '@bariendo/lib/notifications';
import { Alert, Collapse, Container, Group, Stack, Title } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconEye, IconRestore } from '@tabler/icons-react';
import { ReactElement, useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { CommunicationTypeFilter } from '../../components/NotificationTable/CommunicationTypeFilter';
import { NotificationTable } from '../../components/NotificationTable/NotificationTable';
import { useNotificationTable } from '../../components/NotificationTable/useNotificationTable';
import { InboxSortDropdown } from '@/components/InboxSort';
import { useInboxSort } from '@/hooks/useInboxSort';
import { useInboxTracking } from '../../hooks/useInboxTracking';
import { useMedplumProfile } from '@medplum/react';

export function ArchivePage(): ReactElement {
  const medplum = useMedplum();
  const posthog = usePostHog();
  const {
    currentUser,
    currentUserPermissions: { isAdmin },
  } = useCurrentUser();
  const [selectedProvider, setSelectedProvider] = useState<Practitioner | null>(null);
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [sortMode, setSortMode] = useInboxSort(profile?.id);

  const {
    medplum: _medplum,
    typeFilter,
    setTypeFilter,
    searchTerm,
    setSearchTerm,
    notifications,
    loading,
    loadingMore,
    refresh,
    handleScroll,
  } = useNotificationTable({
    notificationStatus: NotificationStatus.ARCHIVED,
    selectedProvider,
    sortMode,
  });

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        id: 'move-to-inbox',
        label: 'Move {count} to inbox',
        icon: <IconRestore size={14} />,
        color: 'blue',
        handler: async (medplum, selectedRefs, onComplete) => {
          await moveNotificationsToInbox(medplum, selectedRefs);
          onComplete();
        },
      },
    ],
    []
  );

  const { handleRowClick } = useInboxTracking({
    posthog,
    page: 'archive',
    typeFilter,
    searchTerm,
    notifications,
    selectedProvider,
    currentUser: currentUser ?? null,
  });

  const isMonitoringOther =
    selectedProvider && currentUser && getReferenceString(selectedProvider) !== getReferenceString(currentUser);

  return (
    <Container
      size="xl"
      py="md"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group align="center">
          <Title order={1}>Archive</Title>
        </Group>

        <Collapse in={!!isMonitoringOther}>
          <Alert icon={<IconEye size={16} />} color="blue" variant="light" mb="lg">
            You're viewing{' '}
            <strong>
              {selectedProvider?.name?.[0]?.given?.join(' ')} {selectedProvider?.name?.[0]?.family}
            </strong>
            's archive. Restricted content opens in the patient's timeline.
          </Alert>
        </Collapse>

        <Group justify="space-between" align="center">
          <CommunicationTypeFilter value={typeFilter} onChange={setTypeFilter} />
          <Group gap="sm">
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
            {isAdmin && (
              <ProviderMonitorDropdown selectedProvider={selectedProvider} onProviderChange={setSelectedProvider} />
            )}
          </Group>
        </Group>

        <NotificationTable
          medplum={medplum}
          notifications={notifications}
          loading={loading}
          loadingMore={loadingMore}
          refresh={refresh}
          handleScroll={handleScroll}
          rowActionKind={RowActionKind.UNARCHIVE}
          toolbarActions={toolbarActions}
          inboxSortControl={<InboxSortDropdown value={sortMode} onChange={setSortMode} />}
          searchTerm={searchTerm}
          onRowClick={(notification, _, defaultHandler) => handleRowClick(notification, defaultHandler)}
        />
      </Stack>
    </Container>
  );
}
